import { useCallback, useEffect, useRef, useState } from 'react';
// material-ui
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Skeleton from '@mui/material/Skeleton';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';

// project-imports
import MainCard from 'components/MainCard';
import Avatar from 'components/@extended/Avatar';
import GlobeIcon from '@mui/icons-material/LanguageRounded';
import PhoneIcon from '@mui/icons-material/PhoneIphoneRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

// assets
import { Call } from 'iconsax-react';
import useApi from 'hooks/useApi';
import { CallStatus, IEventSource } from 'types/auth';
import { IconButton, useTheme } from '@mui/material';
import CallRoundedIcon from '@mui/icons-material/CallRounded';

const POLL_MS = 20000;

const formatDateTime = (value: any): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

// `team.client` may arrive populated or as a bare id; never render an object.
const asText = (value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return value.name || value.uid || '—';
  return String(value);
};

/** Placeholder rows shaped like the real ones, so the list doesn't jump on load. */
const ListSkeleton = () => (
  <List disablePadding sx={{ '& .MuiListItem-root': { px: 2, py: 1.5 } }}>
    {[0, 1, 2, 3, 4].map((i) => (
      <ListItem divider key={i}>
        <ListItemAvatar>
          <Skeleton variant="rounded" width={40} height={40} />
        </ListItemAvatar>
        <ListItemText
          primary={<Skeleton variant="text" width={`${55 + (i % 3) * 12}%`} height={22} />}
          secondary={<Skeleton variant="text" width="35%" height={16} />}
        />
        <Skeleton variant="circular" width={28} height={28} />
      </ListItem>
    ))}
  </List>
);

const DetailsSkeleton = () => (
  <>
    <Skeleton variant="text" width={220} height={34} sx={{ mb: 2 }} />
    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
      <Box key={i} sx={{ mb: 2 }}>
        <Skeleton variant="text" width={110} height={20} />
        <Skeleton variant="text" width={`${40 + (i % 4) * 15}%`} height={20} />
      </Box>
    ))}
  </>
);

const MissedCallLogs = () => {
  // Split deliberately: `isLoading` is the first paint only. The 20s poll sets
  // `isRefreshing`, which shows a hairline bar instead of blanking the list —
  // previously a refresh gave no feedback at all, so the page looked frozen and
  // there was no way to tell how stale the data was.
  const [isLoading, setLoading] = useState(true);
  const [isRefreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [callLogs, setCallLogs] = useState<any[]>([]);
  const [selectedCall, setSelectedCall] = useState<any>(null);
  const [transactionDetails, setTransactionDetails] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const theme = useTheme();
  const { getDashboardCallLogs, getTransactionDetails } = useApi();
  const mountedRef = useRef(true);

  const loadDashboardCallLogs = useCallback(async (background = false) => {
    if (background) setRefreshing(true); else setLoading(true);
    try {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const todayString = `${day}-${month}-${today.getFullYear()}`;

      const res = await getDashboardCallLogs({
        start: todayString, end: todayString, status: CallStatus.MISSED_CALL
      });
      if (!mountedRef.current) return;

      if (res?.success) {
        setCallLogs(Array.isArray(res.data) ? res.data : []);
        setListError(null);
        setLastUpdated(new Date());
      } else {
        // A failed BACKGROUND poll must not wipe good data off the screen.
        if (!background) setListError(res?.message || 'Could not load calls.');
      }
    } catch (err: any) {
      if (!mountedRef.current) return;
      if (!background) setListError(err?.message || 'Could not load calls.');
    } finally {
      if (!mountedRef.current) return;
      setRefreshing(false);
      setLoading(false);
    }
  }, [getDashboardCallLogs]);

  const fetchTransactionDetails = useCallback(async (callLog: any) => {
    setSelectedCall(callLog);
    setTransactionDetails(null);
    setError(null);

    // Without this the pane spun forever: no key meant neither details nor an
    // error was ever set, and the render falls through to the loading branch.
    if (!callLog?.key) {
      setError('This call has no transaction reference to look up.');
      return;
    }

    setDetailsLoading(true);
    try {
      const response = await getTransactionDetails(callLog.key);
      if (!mountedRef.current) return;
      if (response?.data) setTransactionDetails(response.data);
      else setError('No details available for this call.');
    } catch (err: any) {
      if (!mountedRef.current) return;
      setError(err?.message || 'Could not load details for this call.');
    } finally {
      if (mountedRef.current) setDetailsLoading(false);
    }
  }, [getTransactionDetails]);

  useEffect(() => {
    mountedRef.current = true;
    loadDashboardCallLogs();
    const interval = setInterval(() => loadDashboardCallLogs(true), POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [loadDashboardCallLogs]);

  const detailRow = (label: string, value: any) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={500}>{label}</Typography>
      <Typography variant="body2" color="text.secondary">{value}</Typography>
    </Box>
  );

  return (
    <MainCard content={false} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
      <Box sx={{ p: 3, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" fontWeight={700} color="primary.main">Calls</Typography>
          <Box sx={{ flex: 1 }} />
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={() => loadDashboardCallLogs(true)} disabled={isLoading || isRefreshing}>
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" mt={1}>
          View all calls and click on any call to see its transaction details and description.
        </Typography>
      </Box>

      {/* Hairline progress for background refreshes: keeps the list on screen. */}
      <Box sx={{ height: 2 }}>{isRefreshing && <LinearProgress sx={{ height: 2 }} />}</Box>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', p: 3, gap: 3 }}>
        {/* Left panel: list */}
        <Box sx={{ width: 350, minWidth: 250, maxWidth: 400, height: '100%', overflowY: 'auto', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          <Box sx={{ px: 2, py: 2, borderBottom: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="primary.main">
              List of all the calls received
            </Typography>
          </Box>

          {isLoading ? (
            <ListSkeleton />
          ) : listError ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="error.main" gutterBottom>{listError}</Typography>
              <Button size="small" variant="outlined" onClick={() => loadDashboardCallLogs()} sx={{ mt: 1 }}>
                Try again
              </Button>
            </Box>
          ) : callLogs.length === 0 ? (
            <Box sx={{ height: '200px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <Call size="64" color={theme.palette.primary[100]} />
              <Typography mt={2} variant="caption" color={'gray'}>No Missed Calls</Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ '& .MuiListItem-root': { px: 2, py: 1.5 }, maxHeight: '100%', overflow: 'auto' }}>
              {callLogs.map((callLog: any, idx: number) => (
                <ListItem
                  divider
                  key={callLog?.uid || callLog?.key || idx}
                  button
                  selected={selectedCall?.key === callLog?.key}
                  onClick={() => fetchTransactionDetails(callLog)}
                  sx={{ cursor: 'pointer', borderRadius: 2, bgcolor: selectedCall?.key === callLog?.key ? 'primary.lighter' : 'inherit', mb: 1 }}
                >
                  <ListItemAvatar>
                    {callLog?.eventSource === IEventSource.APP ? (
                      <Avatar variant="rounded" type="outlined" color="primary" sx={{ color: 'primary.darker', borderColor: 'primary.light', fontWeight: 600 }}>
                        <PhoneIcon />
                      </Avatar>
                    ) : (
                      <Avatar variant="rounded" type="outlined" color="secondary" sx={{ color: 'secondary.darker', borderColor: 'secondary.light', fontWeight: 600 }}>
                        <GlobeIcon />
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="subtitle1">{callLog?.userName || 'Unknown User'}</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">{callLog?.key}</Typography>}
                  />
                  <IconButton color="primary" disabled={callLog?.eventSource !== IEventSource.APP}>
                    <CallRoundedIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Right panel: details */}
        <Box sx={{ flex: 1, height: '100%', overflowY: 'auto', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, p: 3 }}>
          {!selectedCall ? (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'text.secondary' }}>
              <Call size="48" color={theme.palette.primary[100]} />
              <Typography variant="h6" mt={2}>Select a call to view details</Typography>
            </Box>
          ) : detailsLoading ? (
            <DetailsSkeleton />
          ) : error ? (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'text.secondary' }}>
              <Typography variant="h6" gutterBottom>{error}</Typography>
              {selectedCall?.key && (
                <Button size="small" variant="outlined" onClick={() => fetchTransactionDetails(selectedCall)} sx={{ mt: 1 }}>
                  Try again
                </Button>
              )}
            </Box>
          ) : (
            <>
              <Typography variant="h5" fontWeight={600} color="primary.main" mb={2}>Transaction Details</Typography>
              {detailRow('User', selectedCall?.userName || 'Unknown User')}
              {detailRow('Call Key', selectedCall?.key)}
              {detailRow('Status', asText(transactionDetails?.eventStatus))}
              {detailRow('Client', asText(transactionDetails?.team?.client))}
              {detailRow('Team', asText(transactionDetails?.team?.name))}
              {detailRow('Description', transactionDetails?.description || 'No description available.')}
              {detailRow('Date & Time', formatDateTime(transactionDetails?.updatedAt))}
            </>
          )}
        </Box>
      </Box>
    </MainCard>
  );
};

export default MissedCallLogs;
